import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import AlertTitle from '@mui/material/AlertTitle';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';

import { getRoleById, createRole, updateRole } from '../api/roles';
import { getPermissions } from '../api/permissions';
import { Button, TextField, Card } from '../components/ui-kit';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ConfirmationDialog from '../components/ConfirmationDialog';
import PermissionsPicker from '../components/PermissionsPicker';

// Validation schema
const validationSchema = Yup.object({
  name: Yup.string().required('Role name is required').trim(),
  description: Yup.string(),
});

function RoleEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Check if this is a new role
  const isNewPath = location.pathname.endsWith('/new');
  const isNewRole = id === 'new' || isNewPath;

  // State for managing permissions
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const [conditionsDialog, setConditionsDialog] = useState({ open: false, permission: null, value: '' });
  const [conditionsJsonError, setConditionsJsonError] = useState(false);
  const [removeAllConfirmOpen, setRemoveAllConfirmOpen] = useState(false);

  // State for unsaved changes confirmation
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [nextLocation, setNextLocation] = useState(null);

  // Get available permissions from database
  const { data: { permissions: availablePermissions = [] } = {}, isLoading: permissionsLoading } = useQuery(
    ['permissions', 'role-edit-active'],
    () => getPermissions({ all: true, isCommon: '1', isActive: 'true' }),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  // Get role data when editing
  const {
    data: role,
    isLoading,
    isError,
    error,
  } = useQuery(
    ['role', id],
    () => {
      if (!id || isNewRole) {
        return Promise.reject(new Error('Skip fetching for new role'));
      }
      return getRoleById(id);
    },
    {
      enabled: !isNewRole && !!id,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        
        if (data && Array.isArray(data.permissions)) {
          // Convert permissions to the new format, preserving full structure
          
          const convertedPermissions = data.permissions.map((rolePermission, index) => {
            
            if (rolePermission.permission) {
              // New structure: preserve the full permission object
              return {
                ...rolePermission.permission,
                // Keep the original nested structure for compatibility
                permission: rolePermission.permission,
                // Include role-specific data
                conditions: rolePermission.conditions || {},
                grantedAt: rolePermission.grantedAt,
                _id: rolePermission.permission._id,
              };
            } else {
              // Old structure: permission data is directly in the rolePermission
              return rolePermission;
            }
          });
          
          setSelectedPermissions(convertedPermissions);
        } else {
          setSelectedPermissions([]);
        }
      },
    }
  );

  // Initialize form
  const formik = useFormik({
    initialValues: {
      name: role?.name || '',
      description: role?.description || '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      
      // Validate permissions before saving
      const invalidPermissions = selectedPermissions.filter(permission => {
        
        // Check for new structure: permission.permission.service
        if (permission.permission && permission.permission.service && permission.permission.resource && permission.permission.action) {
          return false; // Valid new format
        } else if (permission.service && permission.resource && permission.action) {
          return false; // Valid old format
        } else if (permission.name && typeof permission.name === 'string') {
          return false; // Valid old format
        } else if (permission.isWildcard && permission.name && permission._id) {
          return false; // Valid wildcard format
        }
        return true; // Invalid
      });
      
      if (invalidPermissions.length > 0) {
        toast.error(`Found ${invalidPermissions.length} invalid permissions. Please review and fix them.`);
        return;
      }
      
      // Transform permissions to the format expected by the backend
      const transformedPermissions = selectedPermissions.map(permission => {
        
        // If it's a wildcard permission created through API, it already has the right structure
        if (permission.isWildcard && permission._id) {
          
          // Ensure _id is a valid ObjectID string
          if (typeof permission._id === 'string' && permission._id.length === 24) {
            return {
              permission: permission._id, // Use the ObjectID from the database
              conditions: permission.conditions || {},
              grantedAt: new Date().toISOString(),
              grantedBy: null // Will be set by backend
            };
          } else {
            throw new Error(`Invalid _id for wildcard permission: ${permission._id}`);
          }
        }
        
        // If it's a regular permission from the database
        if (permission.permission && permission.permission._id) {
          return {
            permission: permission.permission._id,
            conditions: permission.conditions || {},
            grantedAt: new Date().toISOString(),
            grantedBy: null
          };
        }
        
        // If it's a permission object with direct _id
        if (permission._id) {
          return {
            permission: permission._id,
            conditions: permission.conditions || {},
            grantedAt: new Date().toISOString(),
            grantedBy: null
          };
        }
        
        // Should never happen — throw instead of sending malformed data to backend
        throw new Error(`Cannot transform permission: missing _id. Data: ${JSON.stringify(permission)}`);
      });

      const roleData = {
        ...values,
        permissions: transformedPermissions,
      };

      if (isNewRole) {
        createRoleMutation.mutate(roleData);
      } else {
        updateRoleMutation.mutate(roleData);
      }
    },
  });

  // Mutation for creating role
  const createRoleMutation = useMutation((roleData) => {
    return createRole(roleData);
  }, {
    onSuccess: (data) => {
      toast.success('Role created successfully');
      if (data && data._id) {
        navigate(`/roles/${data._id}`);
      } else {
        // If for some reason we don't have an ID, go back to roles list
        navigate('/roles');
      }
    },
    onError: (error) => {
      if (error.response?.status === 409) {
        formik.setFieldError('name', 'Role with this name already exists');
      } else {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Error creating role';
        toast.error(errorMessage);
      }
    },
  });

  // Mutation for updating role
  const updateRoleMutation = useMutation(
    (roleData) => {
      if (!id || id === 'new') {
        throw new Error('Cannot update role: Invalid ID');
      }
      return updateRole(id, roleData);
    },
    {
      onSuccess: () => {
        toast.success('Role updated successfully');
        queryClient.invalidateQueries(['role', id]);
        queryClient.invalidateQueries(['roles']);
        formik.resetForm({ values: formik.values });
      },
      onError: (error) => {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Error updating role';
        toast.error(errorMessage);
      },
    }
  );

  // Helper function to check if form has unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    if (formik.dirty) return true;

    if (isNewRole) {
      return selectedPermissions.length > 0;
    }

    const selectedIds = selectedPermissions.map(p => p._id?.toString()).sort();
    const roleIds = (role?.permissions || []).map(p =>
      (p.permission?._id || p.permission)?.toString()
    ).sort();

    if (JSON.stringify(selectedIds) !== JSON.stringify(roleIds)) return true;

    // Also check if conditions changed for any permission
    const roleConditionsById = {};
    (role?.permissions || []).forEach(p => {
      const id = (p.permission?._id || p.permission)?.toString();
      roleConditionsById[id] = p.conditions || {};
    });

    return selectedPermissions.some(p => {
      const id = p._id?.toString();
      const savedConditions = roleConditionsById[id] || {};
      const currentConditions = p.conditions || {};
      return JSON.stringify(currentConditions) !== JSON.stringify(savedConditions);
    });
  }, [formik.dirty, isNewRole, selectedPermissions, role?.permissions]);

  // Unsaved changes confirmation
  useEffect(() => {
    // Handler for navigation blocking
    const handleBeforeUnload = (event) => {
      if (
        hasUnsavedChanges() &&
        !updateRoleMutation.isSuccess &&
        !createRoleMutation.isSuccess
      ) {
        const message =
          'You have unsaved changes. Are you sure you want to leave?';
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [formik.dirty, selectedPermissions, updateRoleMutation.isSuccess, createRoleMutation.isSuccess, hasUnsavedChanges]);

  // Handle navigation with unsaved changes
  const handleNavigationWithUnsavedChanges = (to) => {
    if (
      hasUnsavedChanges() &&
      !updateRoleMutation.isSuccess &&
      !createRoleMutation.isSuccess
    ) {
      setNextLocation(to);
      setConfirmDialogOpen(true);
    } else {
      navigate(to);
    }
  };

  // Confirm navigation and discard changes
  const confirmNavigation = () => {
    setConfirmDialogOpen(false);
    if (nextLocation) {
      navigate(nextLocation);
    }
  };

  // Remove permission
  const handleRemovePermission = (permissionToRemove) => {
    const updatedPermissions = selectedPermissions.filter(
      permission => permission._id !== permissionToRemove._id
    );
    setSelectedPermissions(updatedPermissions);
    formik.setFieldTouched('name', true); // Mark form as dirty
  };

  // Edit permission conditions
  const handleEditConditions = (permissionToEdit, newConditions) => {
    const updatedPermissions = selectedPermissions.map(permission => {
      if (permission._id === permissionToEdit._id) {
        return {
          ...permission,
          conditions: newConditions
        };
      }
      return permission;
    });
    setSelectedPermissions(updatedPermissions);
    formik.setFieldTouched('name', true);
  };

  // Back to roles list handler
  const handleBack = () => {
    handleNavigationWithUnsavedChanges('/roles');
  };

  if (isLoading && !!id) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError && !isNewRole) {
    return (
      <Box>
        <Typography color="error" variant="h6">
          Error loading data: {error.message}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
          onClick={handleBack}
          aria-label="Return to roles list"
        >
          Back to list
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
      }}
    >
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 4,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          aria-label="Back to roles list"
        >
          Back
        </Button>
        <Typography variant="h4" component="h1" fontWeight="500">
          {isNewRole ? 'Create New Role' : 'Edit Role'}
        </Typography>
      </Box>

      <Card
        sx={{
          p: 3,
          borderRadius: '12px',
          background: (theme) =>
            `linear-gradient(145deg, ${theme.palette.background.paper} 15%, ${theme.palette.background.default} 100%)`,
          boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
          overflow: 'hidden',
          position: 'relative',
          transition: 'all 0.3s ease',
        }}
      >
        <Box component="form" onSubmit={formik.handleSubmit} noValidate>
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              fontWeight: 600,
              position: 'relative',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: 40,
                height: 3,
                borderRadius: '3px',
                bgcolor: 'primary.main',
                backgroundImage: (theme) =>
                  `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
              },
            }}
          >
            Basic Information
          </Typography>

          {/* Warning for system roles */}
          {!isNewRole && role?.isSystem && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>System Role</AlertTitle>
              This is a system role and cannot be edited or deleted. System roles are managed by the application and provide core functionality.
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="name"
                name="name"
                label="Role Name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                disabled={
                  !isNewRole &&
                  role?.isSystem
                }
                inputProps={{
                  'aria-label': 'Role name',
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                id="description"
                name="description"
                label="Description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.description &&
                  Boolean(formik.errors.description)
                }
                helperText={
                  formik.touched.description && formik.errors.description
                }
                disabled={
                  !isNewRole &&
                  role?.isSystem
                }
                inputProps={{
                  'aria-label': 'Role description',
                }}
              />
            </Grid>
          </Grid>

          <Divider
            sx={{
              my: 3,
              opacity: 0.6,
              borderColor: 'divider',
            }}
          />

          <Typography
            variant="h6"
            sx={{
              mb: 3,
              fontWeight: 600,
              position: 'relative',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -8,
                left: 0,
                width: 40,
                height: 3,
                borderRadius: '3px',
                bgcolor: 'info.main',
                backgroundImage: (theme) =>
                  `linear-gradient(to right, ${theme.palette.info.main}, ${theme.palette.info.light})`,
              },
            }}
          >
            Permissions
          </Typography>

          <PermissionsPicker
            availablePermissions={availablePermissions}
            value={selectedPermissions}
            onChange={(next) => {
              setSelectedPermissions(next);
              formik.setFieldTouched('name', true);
            }}
            loading={permissionsLoading}
            disabled={!isNewRole && role?.isSystem}
            onEditConditions={(permission) => {
              if (!isNewRole && role?.isSystem) return;
              const condCount = permission.conditions ? Object.keys(permission.conditions).length : 0;
              setConditionsDialog({
                open: true,
                permission,
                value: condCount > 0 ? JSON.stringify(permission.conditions, null, 2) : '{}',
              });
              setConditionsJsonError(false);
            }}
            renderActions={() => (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  disabled={selectedPermissions.length === 0}
                  onClick={() => setRemoveAllConfirmOpen(true)}
                >
                  Remove All
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="info"
                  disabled={selectedPermissions.length === 0}
                  onClick={() => {
                    const permissionsData = selectedPermissions.map(p => ({
                      id: p._id,
                      name: p.name || `${p.service}.${p.resource}.${p.action}`,
                      service: p.service,
                      resource: p.resource,
                      action: p.action,
                      description: p.description,
                      conditions: p.conditions,
                    }));
                    const dataStr = JSON.stringify(permissionsData, null, 2);
                    const dataBlob = new Blob([dataStr], { type: 'application/json' });
                    const url = URL.createObjectURL(dataBlob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `role-permissions-${role?.name || 'new'}.json`;
                    link.click();
                    URL.revokeObjectURL(url);
                    toast.success('Permissions exported successfully');
                  }}
                >
                  Export
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  onClick={() => {
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.json';
                    input.onchange = (event) => {
                      const file = event.target.files[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = (e) => {
                        try {
                          const importedData = JSON.parse(e.target.result);
                          if (!Array.isArray(importedData)) {
                            toast.error('Invalid file format. Expected JSON array.');
                            return;
                          }
                          const importedPermissions = importedData
                            .map(item => {
                              const byId = item.id
                                ? availablePermissions.find(p => p._id === item.id)
                                : null;
                              if (byId) return byId;
                              // Fallback: ObjectId відрізняється між БД (dev/prod),
                              // тому матчимо за стабільним ключем service.resource.action / name.
                              if (item.name) {
                                const byName = availablePermissions.find(p =>
                                  p.name === item.name ||
                                  `${p.service}.${p.resource}.${p.action}` === item.name
                                );
                                if (byName) return byName;
                              }
                              if (item.service && item.resource && item.action) {
                                return availablePermissions.find(p =>
                                  p.service === item.service &&
                                  p.resource === item.resource &&
                                  p.action === item.action
                                );
                              }
                              return null;
                            })
                            .filter(Boolean);
                          if (importedPermissions.length === 0) {
                            toast.error('No valid permissions found in the imported file');
                            return;
                          }
                          const newPermissions = importedPermissions.filter(
                            imported => !selectedPermissions.some(sel => sel._id === imported._id)
                          );
                          if (newPermissions.length === 0) {
                            toast.info('All imported permissions are already selected');
                            return;
                          }
                          setSelectedPermissions([...selectedPermissions, ...newPermissions]);
                          formik.setFieldTouched('name', true);
                          toast.success(`Imported ${newPermissions.length} new permissions`);
                        } catch {
                          toast.error('Error parsing imported file');
                        }
                      };
                      reader.readAsText(file);
                    };
                    input.click();
                  }}
                >
                  Import
                </Button>
              </Stack>
            )}
          />

          {/* Warning for invalid permissions */}
          {(() => {
            const invalidPermissions = selectedPermissions.filter(permission => {
              if (permission.permission && permission.permission.service && permission.permission.resource && permission.permission.action) return false;
              if (permission.service && permission.resource && permission.action) return false;
              if (permission.name && typeof permission.name === 'string') return false;
              return true;
            });
            if (invalidPermissions.length === 0) return null;
            return (
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Warning:</strong> Found {invalidPermissions.length} invalid permission(s). Please review and remove them.
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {invalidPermissions.map((permission, index) => (
                    <Chip
                      key={index}
                      label={`Invalid: ${JSON.stringify(permission).substring(0, 50)}...`}
                      size="small"
                      color="error"
                      onDelete={() => handleRemovePermission(permission)}
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </Box>
              </Alert>
            );
          })()}

          <Box sx={{ mt: 4 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={
                updateRoleMutation.isLoading ||
                createRoleMutation.isLoading ||
                (!isNewRole && role?.isSystem) ||
                (isNewRole && !formik.values.name.trim()) ||
                !hasUnsavedChanges()
              }
              aria-label="Save role"
            >
              {updateRoleMutation.isLoading || createRoleMutation.isLoading ? (
                <CircularProgress size={24} />
              ) : isNewRole ? (
                'Create Role'
              ) : (
                'Save Changes'
              )}
            </Button>
            {formik.dirty && (
              <Typography
                variant="caption"
                color="primary"
                sx={{
                  ml: 2,
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                You have unsaved changes
              </Typography>
            )}
          </Box>
        </Box>
      </Card>

      {/* Conditions Dialog */}
      <Dialog
        open={conditionsDialog.open}
        onClose={() => setConditionsDialog({ open: false, permission: null, value: '' })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {conditionsDialog.permission && (() => {
            const p = conditionsDialog.permission;
            if (p.permission?.service) return `Conditions: ${p.permission.service}.${p.permission.resource}.${p.permission.action}`;
            if (p.service) return `Conditions: ${p.service}.${p.resource}.${p.action}`;
            return `Conditions: ${p.name || 'Permission'}`;
          })()}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Enter conditions as JSON. Leave as <code>{'{}'}</code> for no conditions.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={8}
            value={conditionsDialog.value}
            onChange={(e) => {
              setConditionsDialog(prev => ({ ...prev, value: e.target.value }));
              setConditionsJsonError(false);
            }}
            error={conditionsJsonError}
            helperText={conditionsJsonError ? 'Invalid JSON format' : ''}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConditionsDialog({ open: false, permission: null, value: '' })} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              try {
                const parsed = JSON.parse(conditionsDialog.value);
                handleEditConditions(conditionsDialog.permission, parsed);
                toast.success('Conditions updated');
                setConditionsDialog({ open: false, permission: null, value: '' });
              } catch {
                setConditionsJsonError(true);
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove All Permissions Confirmation */}
      <ConfirmationDialog
        open={removeAllConfirmOpen}
        onClose={() => setRemoveAllConfirmOpen(false)}
        onConfirm={() => {
          setSelectedPermissions([]);
          formik.setFieldTouched('name', true);
          toast.success('All permissions removed');
          setRemoveAllConfirmOpen(false);
        }}
        title="Remove All Permissions"
        contentText="Are you sure you want to remove all permissions from this role?"
        confirmButtonText="Remove All"
        confirmButtonColor="error"
        cancelButtonText="Cancel"
      />

      {/* Unsaved Changes Dialog using ConfirmationDialog component */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={confirmNavigation}
        title="Unsaved Changes"
        contentText="You have unsaved changes. Are you sure you want to leave this page? All unsaved changes will be lost."
        confirmButtonText="Discard Changes"
        confirmButtonColor="error"
        cancelButtonText="Stay"
      />
    </Box>
  );
}

export default RoleEdit;
